import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";
import { api, config } from "../lib/api.js";
import { PageHeader } from "../components/PageHeader.jsx";
import { EmptyState } from "../components/EmptyState.jsx";

export function Newsletter() {
  const list = useQuery({ queryKey: ["newsletters"], queryFn: api.newsletters, enabled: config.hasBase });
  const [slug, setSlug] = useState(null);
  const issues = useQuery({
    queryKey: ["issues", slug],
    queryFn: () => api.newsletterIssues(slug),
    enabled: !!slug
  });
  const [ideas, setIdeas] = useState(null);
  const [busy, setBusy] = useState(false);

  if (!config.hasBase) return <EmptyState title="Backend not configured" description="Configure VITE_API_BASE / VITE_API_TOKEN." />;

  async function genIdeas() {
    setBusy(true);
    try { setIdeas(await api.newsletterIdeas()); }
    catch (e) { setIdeas({ error: e.message }); }
    finally { setBusy(false); }
  }

  const newsletters = list.data?.newsletters || [];
  if (slug == null && newsletters.length > 0) {
    setSlug(newsletters[0].slug);
  }

  return (
    <>
      <PageHeader
        title="Newsletter"
        actions={<button className="btn btn-primary" disabled={busy} onClick={genIdeas}>{busy ? "Generating…" : "Generate ideas"}</button>}
      />

      {ideas && (
        <div className="card mb-4">
          <div className="font-medium mb-2">Newsletter ideas</div>
          {ideas.error ? <div className="text-rose-400 text-sm">{ideas.error}</div> : (
            <ul className="space-y-3">
              {ideas.ideas?.map((idea, i) => (
                <li key={i}>
                  <div className="font-medium">{idea.title}</div>
                  <ul className="list-disc list-inside text-sm subtle mt-1 space-y-0.5">
                    {idea.outline?.map((s, j) => <li key={j}>{s}</li>)}
                  </ul>
                  <div className="text-xs subtle mt-1">{idea.rationale}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="card">
          <div className="font-medium mb-2">Your newsletters</div>
          {list.isLoading ? <div className="subtle">Loading…</div> : newsletters.length === 0 ? (
            <div className="subtle text-sm">Visit a newsletter issue you own with the extension installed to seed this list.</div>
          ) : (
            <ul className="space-y-1">
              {newsletters.map((n) => (
                <li key={n.slug}>
                  <button className={`text-left w-full px-2 py-1 rounded ${slug === n.slug ? "bg-white/10" : "hover:bg-white/5"}`}
                    onClick={() => setSlug(n.slug)}>
                    {n.name}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="card lg:col-span-2">
          <div className="font-medium mb-2">Issue performance</div>
          {!slug ? <div className="subtle">Select a newsletter.</div> : issues.isLoading ? <div className="subtle">Loading…</div> : (
            <>
              <div className="h-56 mb-4">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={[...(issues.data?.issues || [])].reverse()}>
                    <CartesianGrid stroke="#1f2630" strokeDasharray="3 3" />
                    <XAxis dataKey="title" stroke="#6b7280" hide />
                    <YAxis stroke="#6b7280" />
                    <Tooltip contentStyle={{ background: "#0e1119", border: "1px solid #1f2630" }} />
                    <Bar dataKey="like_count" fill="#0a66c2" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <ul className="text-sm space-y-2">
                {(issues.data?.issues || []).map((i) => (
                  <li key={i.issue_url} className="flex justify-between border-b border-white/5 pb-2">
                    <span className="line-clamp-1">{i.title}</span>
                    <span className="subtle">{i.like_count} · {i.comment_count} · {i.view_count}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </div>
    </>
  );
}
